// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The shared clock for the Live view's measured curves.
 *
 * Every live series polls on the same cadence and asks for the same window end.
 * The rounding keeps that end stable between polls, so a window computed a few
 * hundred milliseconds apart is the same window rather than two.
 *
 * The interval and the re-read overlap are tunables and live in
 * {@link ../config/queryConfig}; they are re-exported here so callers keep
 * importing the poll clock from one place.
 */

import { LIVE_POLL_INTERVAL_MS, LIVE_REFETCH_OVERLAP_MS } from '../config/queryConfig';

export { LIVE_POLL_INTERVAL_MS, LIVE_REFETCH_OVERLAP_MS };

/**
 * `now` snapped back to the poll boundary.
 *
 * The one place that rounding happens. Everything that has to agree with the
 * fetch window — the NOW cursor, the active phase — reads it from here rather
 * than re-deriving it, so tuning the interval moves the data and the chrome
 * together instead of drifting them apart.
 *
 * A non-positive or non-finite interval means "as fast as it answers", so there
 * is no boundary to snap to and `now` is returned as-is. Dividing by it would
 * otherwise yield an Invalid Date, which `toISOString` turns into a throw.
 */
export function pollWindowInstant(now: Date): Date {
  if (!Number.isFinite(LIVE_POLL_INTERVAL_MS) || LIVE_POLL_INTERVAL_MS <= 0) return now;
  return new Date(Math.floor(now.getTime() / LIVE_POLL_INTERVAL_MS) * LIVE_POLL_INTERVAL_MS);
}

/** {@link pollWindowInstant} as an ISO-8601 timestamp. */
export function pollWindowEnd(now: Date): string {
  return pollWindowInstant(now).toISOString();
}

/**
 * Where the next poll should start reading: back off from what has already been
 * fetched by the overlap, but never before the axis start.
 *
 * Returns `axisStart` when nothing has been fetched yet, so a cold entry reads
 * the whole day and a warm one reads only the tail.
 */
export function deltaWindowStart(axisStart: string, fetchedThrough: string | undefined): string {
  if (!fetchedThrough) return axisStart;
  const from = Date.parse(fetchedThrough) - LIVE_REFETCH_OVERLAP_MS;
  const floor = Date.parse(axisStart);
  if (!Number.isFinite(from) || !Number.isFinite(floor)) return axisStart;
  return from <= floor ? axisStart : new Date(from).toISOString();
}

/** The longest a failing Live poll waits between attempts: five minutes. */
export const LIVE_BACKOFF_MAX_MS = 5 * 60_000;

/** Failures after which the wait stops doubling (2^3 = 8 times the interval). */
const LIVE_BACKOFF_MAX_DOUBLINGS = 3;

/**
 * Whether a failed read says the upstream is struggling: a 5xx, a network
 * error with no status, or a 408/429 (timed out, rate limited: asking again
 * sooner is exactly what it is pushing back on). Any other 4xx is the server's
 * answer about this request, and asking again later will not change it, so it
 * does not slow the poll.
 */
export function isServerFault(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status !== 'number' || status >= 500 || status === 408 || status === 429;
}

/**
 * The wait before the next Live poll, given how many polls in a row failed with
 * a server fault.
 *
 * Zero failures keeps the normal cadence. Each consecutive failure doubles the
 * wait, up to eight times the interval and never past {@link LIVE_BACKOFF_MAX_MS}
 * (an interval already longer than that is left as it is), so a struggling
 * telemetry service is asked less often instead of more. One success resets it.
 */
export function liveBackoffInterval(failures: number): number {
  const base = LIVE_POLL_INTERVAL_MS;
  if (!Number.isFinite(base) || base <= 0 || failures <= 0) return base;
  const waited = base * 2 ** Math.min(failures, LIVE_BACKOFF_MAX_DOUBLINGS);
  return Math.max(base, Math.min(waited, LIVE_BACKOFF_MAX_MS));
}
